import { useCallback, useState } from "react";
import {
  Volume2,
  Square,
  BookOpen,
  BookmarkPlus,
  BookmarkCheck,
  GraduationCap,
  StickyNote,
  Loader2,
} from "lucide-react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useTextSelection } from "@/hooks/use-text-selection";
import { useSpeakText } from "@/hooks/use-speak-text";
import { useSavedVocabulary } from "@/hooks/use-saved-vocabulary";
import { useDictionaryStore } from "@/stores/dictionary";
import { useAIAssistant } from "@/contexts/AIAssistantContext";
import { WordNotes } from "@/components/vocabulary/WordNotes";
import { useTranslation } from "react-i18next";

/**
 * Floating toolbar that appears next to any text the user highlights, offering
 * quick actions: Listen, Define, Save, Ask Coach, and Take notes. Mounted once
 * (document-wide) in {@link AppContent}. All actions reuse existing hooks/stores.
 */
export const TextSelectionToolbar = () => {
  const { t } = useTranslation();
  const { text, isOpen, virtualRef, dismiss } = useTextSelection();
  const { handleSpeak, isSpeaking, isLoading } = useSpeakText();
  const { saveVocabulary, isSaving, isWordSaved } = useSavedVocabulary();
  const { toast } = useToast();

  // Dictionary store (Define / Save). Select slices to avoid re-rendering on
  // unrelated store changes.
  const setSearchValue = useDictionaryStore((s) => s.setSearchValue);
  const fetchDefinition = useDictionaryStore((s) => s.fetchDefinition);
  const openDictionary = useDictionaryStore((s) => s.openWidget);
  const language = useDictionaryStore((s) => s.language);

  // AI assistant (Ask Coach).
  const { openWidget: openCoach, sendMessage } = useAIAssistant();

  // Note dialog — holds the word at the moment the dialog was opened so it
  // survives the selection collapsing.
  const [noteWord, setNoteWord] = useState<string | null>(null);

  const saved = text.trim() ? isWordSaved(text.trim()) : false;

  const handleListen = useCallback(
    (e: React.MouseEvent) => {
      handleSpeak(text, e);
    },
    [handleSpeak, text]
  );

  const handleDefine = useCallback(() => {
    const word = text.trim();
    setSearchValue(word);
    fetchDefinition(word, language);
    openDictionary();
    dismiss();
  }, [text, setSearchValue, fetchDefinition, language, openDictionary, dismiss]);

  const handleSave = useCallback(async () => {
    const word = text.trim();
    if (!word || isWordSaved(word)) return; // already saved — no-op
    dismiss();
    try {
      // Always look up the English definition for saving, regardless of the
      // current dictionary language — saved vocabulary should store the
      // English definition + example so it is useful for review.
      await fetchDefinition(word, "en");
      const entry = useDictionaryStore.getState().entry;
      if (!entry) {
        // No English definition found — fall back to the dictionary widget so
        // the user can search/adjust before saving.
        setSearchValue(word);
        openDictionary();
        return;
      }

      const firstMeaning = entry.meanings[0];
      const firstDefinition = firstMeaning?.definitions[0];
      await saveVocabulary({
        word: entry.word,
        definition: firstDefinition?.definition || "",
        example: firstDefinition?.example || "",
        ipa: entry.phonetic || entry.phonetics.find((p) => p.text)?.text,
        type: firstMeaning?.partOfSpeech,
      });

      toast({
        title: "Word saved!",
        description: `"${entry.word}" has been added to your vocabulary.`,
      });
    } catch (err) {
      toast({
        title: "Failed to save",
        description:
          err instanceof Error ? err.message : "Could not save vocabulary",
        variant: "destructive",
      });
    }
  }, [
    text,
    dismiss,
    fetchDefinition,
    setSearchValue,
    openDictionary,
    saveVocabulary,
    isWordSaved,
    toast,
  ]);

  const handleAskCoach = useCallback(() => {
    const phrase = text.trim();
    dismiss();
    openCoach();
    sendMessage(
      `Explain "${phrase}" in detail. Include its meaning, common usages, example sentences, and any synonyms or related words.`
    );
  }, [text, dismiss, openCoach, sendMessage]);

  const handleOpenNotes = useCallback(() => {
    setNoteWord(text.trim());
    dismiss();
  }, [text, dismiss]);

  return (
    <>
      <Popover open={isOpen} onOpenChange={(open) => !open && dismiss()}>
        <PopoverAnchor virtualRef={virtualRef} />
        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          // Keep the text selection in place — Radix would otherwise move focus
          // into the content and collapse it.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="flex w-auto items-center gap-0.5 rounded-full border border-border/60 bg-popover/85 p-1 shadow-xl shadow-black/10 backdrop-blur-xl supports-[backdrop-filter]:bg-popover/70"
          data-selection-toolbar-ignore=""
        >
          <ToolbarButton
            label={isSpeaking || isLoading ? t("pages.dialogs.stop", "Stop") : t("pages.dialogs.listen", "Listen")}
            onClick={handleListen}
            accentClassName="hover:text-sky-500"
            active={isSpeaking || isLoading}
            icon={
              isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isSpeaking ? (
                <Square className="h-3.5 w-3.5 fill-current" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )
            }
          />

          <Divider />

          <ToolbarButton
            label={t("pages.dialogs.define", "Define")}
            onClick={handleDefine}
            accentClassName="hover:text-violet-500"
            icon={<BookOpen className="h-4 w-4" />}
          />

          <ToolbarButton
            label={saved ? t("pages.dialogs.saved", "Saved") : t("pages.dialogs.save", "Save")}
            onClick={handleSave}
            disabled={isSaving}
            accentClassName="hover:text-emerald-500"
            active={saved}
            activeClassName="bg-emerald-500/10 text-emerald-500"
            icon={
              isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <BookmarkCheck className="h-4 w-4" />
              ) : (
                <BookmarkPlus className="h-4 w-4" />
              )
            }
          />

          <Divider />

          <ToolbarButton
            label={t("pages.dialogs.askCoach", "Ask Coach")}
            onClick={handleAskCoach}
            accentClassName="hover:text-amber-500"
            icon={<GraduationCap className="h-4 w-4" />}
          />

          <ToolbarButton
            label={t("pages.dialogs.notes", "Notes")}
            onClick={handleOpenNotes}
            accentClassName="hover:text-rose-500"
            icon={<StickyNote className="h-4 w-4" />}
          />
        </PopoverContent>
      </Popover>

      <Dialog
        open={noteWord !== null}
        onOpenChange={(open) => !open && setNoteWord(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          data-selection-toolbar-ignore=""
        >
          <DialogHeader>
            <DialogTitle className="truncate">Notes — {noteWord}</DialogTitle>
          </DialogHeader>
          {noteWord && (
            <WordNotes word={noteWord} variant="panel" allowCreate />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Divider = () => (
  <span aria-hidden className="mx-0.5 h-5 w-px bg-border/60" />
);

interface ToolbarButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  /** Tailwind hover color, e.g. "hover:text-sky-500". */
  accentClassName?: string;
  /** Renders the accent color persistently (e.g. while speaking or saved). */
  active?: boolean;
  /** Style applied when `active`. Defaults to a sky accent. */
  activeClassName?: string;
}

const ToolbarButton = ({
  label,
  icon,
  onClick,
  disabled,
  accentClassName,
  active,
  activeClassName = "bg-sky-500/10 text-sky-500",
}: ToolbarButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    className={cn(
      "flex h-8 items-center gap-1.5 rounded-full px-2.5 text-muted-foreground transition-colors duration-150 hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50",
      accentClassName,
      active && activeClassName
    )}
  >
    {icon}
    <span className="text-xs font-medium">{label}</span>
  </button>
);
